import { logError } from 'fergy-core-react-logging';
import { type FunctionComponent, type MouseEventHandler, type KeyboardEventHandler, type FocusEventHandler } from 'react';
import { type AnalyticsEvent, AnalyticsHelper } from '../../../helpers/analytics/analytics.helper';
import { generateDataSelector } from '../../../helpers/general-helper/general-helper';
import { BUTTON_SIZE_MAP, type ButtonSize, type ButtonStyle } from '../button.types';

export type BaseButtonProps = {
	// Analytics data that will sent upon clicking the Link.
	analyticsEvent?: AnalyticsEvent;
	buttonStyle?: ButtonStyle;
	ariaLabel?: string;
	automationHook?: string;
	analyticsHook?: string;
	buttonType?: 'button' | 'reset' | 'submit';
	children?: string | JSX.Element | string[];
	className?: string;
	id?: string;
	onClick?: MouseEventHandler<HTMLButtonElement>;
	onKeyDown?: KeyboardEventHandler<HTMLButtonElement>;
	onBlur?: FocusEventHandler<HTMLButtonElement>;
	size?: ButtonSize;
	disabled?: boolean;
	tabIndex?: 0 | -1;
	testId?: string;
	title?: string;
	useBorderRadius?: boolean;
};

/**
 * Basic Component that all other buttons should compose.
 *
 * @abstract
 */
export const BaseButton: FunctionComponent<BaseButtonProps> = ({
	analyticsEvent,
	ariaLabel,
	automationHook,
	analyticsHook,
	buttonType = 'button',
	children,
	className,
	id,
	onClick,
	onKeyDown,
	onBlur,
	size = 'DEFAULT',
	disabled,
	testId,
	tabIndex,
	title,
	useBorderRadius = true
}) => {
	const clickHandler = analyticsEvent
		? (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
				AnalyticsHelper.track(analyticsEvent.data)
					.catch(logError)
					.finally(() => {
						if (onClick) {
							onClick(e);
						}
					});
		  }
		: onClick;

	return (
		<button
			className={`${disabled ? 'not-allowed' : 'pointer'} ba ${
				size === 'CIRCLE' || !useBorderRadius ? '' : 'br2'
			} w-inherit ${className} ${size ? BUTTON_SIZE_MAP[size] : ''}`}
			id={id}
			onClick={clickHandler}
			onKeyDown={onKeyDown}
			onBlur={onBlur}
			aria-label={ariaLabel}
			type={buttonType}
			disabled={disabled}
			tabIndex={tabIndex}
			title={title}
			data-automation={generateDataSelector('button', automationHook)}
			data-analytics={generateDataSelector('button', analyticsHook)}
			data-testid={testId}>
			{children}
		</button>
	);
};
